/**
 * Brand onboarding — pieces shared by the three steps (BrandSetupStep, MarketStep, PromptsStep).
 * Moved out of BrandOnboarding.tsx with the 3-page rewrite (2026-11-03).
 */
import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputBase,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { Envergure, IndustryCode } from '@traaker/shared';
import { INDUSTRY_CODES, INDUSTRY_LABELS_EN, INDUSTRY_LABELS_FR } from '@traaker/shared';
import { useTranslation } from 'react-i18next';
import { useDomainFavicon } from '../../hooks/useDomainFavicon';

// Themed onboarding backdrop — matches the account-onboarding Shell (Onboarding.tsx).
export const ONBOARDING_BG =
  'radial-gradient(1000px 480px at 50% -8%, rgba(124,58,237,0.12), transparent 60%), ' +
  'linear-gradient(180deg, #FAFAFA 0%, #F4F2FB 100%)';

// ── Industry / business model / envergure ───────────────────────────────────

export function foldLower(s: string): string {
  return (s || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

// Map a free-text industry (from discovery, e.g. "SaaS", "logiciel B2B",
// "cabinet de conseil") to an IndustryCode. Aliases fold accents + cover common
// English/French phrasings so the discovered draft snaps to the right value.
const INDUSTRY_ALIASES: Partial<Record<IndustryCode, string[]>> = {
  saas_b2b: ['saas', 'logiciel', 'software', 'application', 'app', 'plateforme', 'platform', 'b2b'],
  ecommerce: ['e-commerce', 'ecommerce', 'commerce en ligne', 'boutique en ligne', 'vente en ligne', 'dropshipping'],
  retail: ['retail', 'commerce', 'magasin', 'boutique', 'distribution', 'grande distribution'],
  restauration: ['restauration', 'restaurant', 'food', 'traiteur', 'cafe', 'bar'],
  tourism_hospitality: ['hotellerie', 'hotel', 'hospitality', 'tourisme', 'voyage', 'gite', 'camping'],
  health: ['sante', 'health', 'medical', 'medecine', 'pharma', 'biotech', 'clinique', 'bien-etre'],
  legal_services: ['juridique', 'avocat', 'legal', 'droit', 'notaire', 'cabinet juridique', 'legaltech'],
  real_estate: ['immobilier', 'real estate', 'proptech', 'foncier', 'agence immobiliere'],
  manufacturing: ['industrie', 'manufacturing', 'fabrication', 'usine', 'production', 'industriel'],
  construction: ['construction', 'btp', 'batiment', 'travaux', 'renovation', 'architecte'],
  finance_banking: ['finance', 'banque', 'bank', 'fintech', 'investissement', 'credit', 'comptabilite'],
  insurance: ['assurance', 'insurance', 'assurtech', 'mutuelle', 'courtier'],
  media_publishing: ['media', 'presse', 'edition', 'publishing', 'journal', 'magazine'],
  marketing_agency: ['marketing', 'agence', 'agency', 'communication', 'publicite', 'advertising', 'seo', 'growth'],
  education_training: ['education', 'formation', 'training', 'edtech', 'ecole', 'school', 'apprentissage', 'universite'],
  energy: ['energie', 'energy', 'renouvelable', 'solaire', 'eolien', 'petrole', 'gaz', 'cleantech'],
  transport_logistics: ['transport', 'logistique', 'logistics', 'livraison', 'fret', 'supply chain', 'mobilite'],
  automotive: ['automobile', 'auto', 'automotive', 'voiture', 'garage', 'concessionnaire'],
  fashion_beauty: ['mode', 'fashion', 'beaute', 'beauty', 'cosmetique', 'vetement', 'luxe'],
  sports_leisure: ['sport', 'loisirs', 'fitness', 'salle de sport', 'jeu', 'gaming', 'divertissement'],
  b2b_services: ['services aux entreprises', 'conseil', 'consulting', 'cabinet', 'b2b services', 'rh', 'recrutement'],
  b2c_services: ['services aux particuliers', 'services a la personne', 'artisan', 'depannage', 'menage'],
  consumer_tech: ['tech', 'technologie', 'technology', 'hardware', 'iot', 'electronique', 'gadget', 'objets connectes'],
  public_sector: ['secteur public', 'public', 'administration', 'collectivite', 'gouvernement', 'ong', 'association'],
};

export function matchIndustry(raw: string): IndustryCode | null {
  const needle = foldLower(raw).trim();
  if (!needle) return null;
  // Exact code or label match first (accent/case-insensitive).
  const byCode = INDUSTRY_CODES.find((c) => c === needle);
  if (byCode) return byCode;
  const byLabel = INDUSTRY_CODES.find(
    (c) => foldLower(INDUSTRY_LABELS_FR[c]) === needle || foldLower(INDUSTRY_LABELS_EN[c]) === needle,
  );
  if (byLabel) return byLabel;
  // Synonym/substring match — both directions so "saas b2b" and "logiciel" hit.
  for (const code of INDUSTRY_CODES) {
    const aliases = INDUSTRY_ALIASES[code] || [];
    if (aliases.some((a) => needle.includes(a) || a.includes(needle))) return code;
  }
  return null;
}

// `labelKey` resolved with `t()` at render. B2B/B2C stay verbatim (proper terms).
export const BUSINESS_MODELS: { value: 'b2b' | 'b2c' | 'both'; labelKey: string }[] = [
  { value: 'b2b', labelKey: 'onboarding.knowledge.businessModel.b2b' },
  { value: 'b2c', labelKey: 'onboarding.knowledge.businessModel.b2c' },
  { value: 'both', labelKey: 'onboarding.knowledge.businessModel.both' },
];

export const ENVERGURE_STEPS: { value: Envergure; labelKey: string }[] = [
  { value: 'hyper-local', labelKey: 'onboarding.knowledge.envergure.hyperLocal' },
  { value: 'multi-local', labelKey: 'onboarding.knowledge.envergure.multiLocal' },
  { value: 'mono-country', labelKey: 'onboarding.knowledge.envergure.national' },
  { value: 'multi-country', labelKey: 'onboarding.knowledge.envergure.multiCountry' },
  { value: 'global', labelKey: 'onboarding.knowledge.envergure.global' },
];

// ── Competitor domains ──────────────────────────────────────────────────────

// True-ish domain detector: "lemonde.fr" → "lemonde.fr"; "Le Monde" → null.
export function domainIfLooksLikeOne(s: string): string | null {
  const t = (s || '').trim().toLowerCase();
  if (/^https?:\/\//.test(t)) {
    try {
      return new URL(t).hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }
  // Bare host heuristic: contains a dot, no spaces, valid-ish TLD.
  if (!t.includes(' ') && /^[a-z0-9.-]+\.[a-z]{2,}$/.test(t)) {
    return t.replace(/^www\./, '');
  }
  return null;
}

// Normalize a user-typed competitor domain: lowercase, strip scheme/path/www.
export function normalizeCompetitorDomain(input: string): string {
  let d = (input || '').trim().toLowerCase();
  if (!d) return '';
  d = d.replace(/^https?:\/\//, '').split('/')[0].split('?')[0].split('#')[0];
  return d.replace(/^www\./, '');
}

// Mirror of the backend `looksLikeDomain` + Competitors page `isValidDomain`.
export function isValidCompetitorDomain(domain: string): boolean {
  const d = (domain || '').trim().toLowerCase();
  if (!d) return false;
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d) && /\.[a-z]{2,}$/.test(d);
}

// Display name from a domain: "lemonde.fr" → "Lemonde". Best-effort label only.
export function competitorNameFromDomain(domain: string): string {
  const label = (domain.split('.')[0] || domain).replace(/[-_]/g, ' ').trim();
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : domain;
}

// ── Small UI pieces ─────────────────────────────────────────────────────────

// Favicon for a bare domain (used pre-brand-creation, where BrandFavicon's
// brandId doesn't exist yet). Falls back to a storefront glyph.
export function DomainLogo({ domain, size = 48 }: { domain: string; size?: number }) {
  const src = useDomainFavicon(domain || undefined, 64);
  return (
    <Avatar
      src={src || undefined}
      variant="rounded"
      sx={{ width: size, height: size, bgcolor: 'action.hover', color: 'text.secondary', borderRadius: 2.5 }}
    >
      <StorefrontIcon sx={{ fontSize: size * 0.6 }} />
    </Avatar>
  );
}

/** ⓘ next to a title, the explanation on hover. */
export function InfoTip({ title }: { title: React.ReactNode }) {
  return (
    <Tooltip title={title} arrow placement="top">
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
    </Tooltip>
  );
}

/** The closed-by-default « Options avancées » row (pages 1 and 2) and its body. */
export function AdvancedToggle({
  open,
  onToggle,
  count,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  count?: number;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, width: '100%', px: 4, py: 2.25,
          border: 'none', background: 'none', cursor: 'pointer', color: 'text.secondary',
          fontWeight: 600, fontSize: 13.5, textAlign: 'left', fontFamily: 'inherit',
          '&:hover': { color: 'primary.main' },
        }}
      >
        {t('onboarding.common.advanced')}
        {!!count && (
          <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main', bgcolor: 'primary.50', borderRadius: 999, px: 1, py: 0.25 }}>
            {count}
          </Box>
        )}
        <Box component="span" sx={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }}>⌄</Box>
      </Box>
      {open && <Box sx={{ px: 4, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</Box>}
    </Box>
  );
}

/** A bordered sub-section inside « Options avancées ». */
export function SubCard({ icon, title, info, children }: { icon: React.ReactNode; title: string; info?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3.5, p: 2, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <Box sx={{ width: 30, height: 30, borderRadius: 2.25, display: 'grid', placeItems: 'center', bgcolor: 'primary.50', color: 'primary.main' }}>
          {icon}
        </Box>
        <Box sx={{ fontWeight: 700, fontSize: 14.5 }}>{title}</Box>
        {info && <InfoTip title={info} />}
      </Box>
      {children}
    </Box>
  );
}

export interface ChoiceItem {
  text: string;
  /** Selectable lists only: whether the item is kept on save. */
  on: boolean;
}

/**
 * An editable list: rows (optionally with a check box), ✕ on hover, « + Ajouter » input
 * (Enter adds, case-insensitive dedupe, `maxChars` bound).
 */
export function ChoiceList({
  items,
  onChange,
  selectable = false,
  maxChars,
  maxItems,
  placeholder,
}: {
  items: ChoiceItem[];
  onChange: (items: ChoiceItem[]) => void;
  selectable?: boolean;
  maxChars: number;
  maxItems?: number;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    setInput('');
    if (!v || items.some((i) => foldLower(i.text) === foldLower(v))) return;
    if (maxItems !== undefined && items.length >= maxItems) return;
    onChange([...items, { text: v, on: true }]);
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {items.map((it, i) => (
        <Box
          key={`${it.text}-${i}`}
          onClick={selectable ? () => onChange(items.map((x, j) => (j === i ? { ...x, on: !x.on } : x))) : undefined}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.1, px: 1.4, py: 1, borderRadius: 2.5, fontSize: 13, lineHeight: 1.45,
            border: '1px solid', borderColor: selectable && it.on ? 'rgba(124,58,237,.22)' : 'divider',
            bgcolor: selectable && it.on ? '#faf8ff' : 'background.paper',
            cursor: selectable ? 'pointer' : 'default',
            '&:hover .choice-del': { visibility: 'visible' },
          }}
        >
          {selectable && (
            <Box sx={{
              width: 18, height: 18, borderRadius: 1.25, flexShrink: 0, display: 'grid', placeItems: 'center', color: '#fff',
              border: '1.5px solid', borderColor: it.on ? 'primary.main' : '#d4d4d8', bgcolor: it.on ? 'primary.main' : 'transparent',
            }}>
              {it.on && <CheckIcon sx={{ fontSize: 13 }} />}
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>{it.text}</Box>
          <IconButton
            className="choice-del"
            size="small"
            aria-label={t('onboarding.common.remove')}
            onClick={(e) => { e.stopPropagation(); onChange(items.filter((_, j) => j !== i)); }}
            sx={{ visibility: 'hidden', p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
          >
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>
      ))}
      {(maxItems === undefined || items.length < maxItems) && (
        <Box sx={{ px: 1.4, py: 0.75, borderRadius: 2.5, border: '1px dashed', borderColor: 'divider' }}>
          <InputBase
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            onBlur={add}
            placeholder={placeholder ?? t('onboarding.common.addPlaceholder')}
            inputProps={{ maxLength: maxChars }}
            sx={{ fontSize: 13 }}
          />
        </Box>
      )}
    </Box>
  );
}

// Shared "Quitter" control for the wizard steps — deletes the brand being
// onboarded and returns to Settings → Marques. Only rendered when the parent
// determines `canQuit` (not the tenant's only path into the app, admin role).
export function QuitOnboardingButton({ brandName, onQuit }: { brandName?: string; onQuit: () => Promise<void> }) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [quitting, setQuitting] = useState(false);

  const handleConfirm = async () => {
    setQuitting(true);
    try {
      await onQuit();
    } finally {
      setQuitting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Button variant="text" onClick={() => setConfirmOpen(true)}>
        {t('onboarding.common.quit')}
      </Button>
      <Dialog open={confirmOpen} onClose={() => !quitting && setConfirmOpen(false)}>
        <DialogTitle>{t('onboarding.common.quitConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('onboarding.common.quitConfirmBody', { name: brandName || t('onboarding.wizard.yourBrand') })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={quitting}>
            {t('onboarding.common.cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirm} disabled={quitting}>
            {quitting ? <CircularProgress size={20} color="inherit" /> : t('onboarding.common.quitConfirmProceed')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
